// Approver Portal — Federate OIDC Authentication
// Authorization Code Flow with PKCE (same as main portal)

const FEDERATE_AUTHORIZE = 'https://idp.federate.amazon.com/api/oauth2/v1/authorize';
const CLIENT_ID = 'pam-approver-auth';
const REDIRECT_URI = window.location.origin + '/';
const API_BASE_URL = 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';

const SESSION_KEY = 'pam_approver_auth_session';
const ATTEMPTED_KEY = 'approver_auth_attempted';
const RETURN_URL_KEY = 'approver_auth_return_url';

// ════════════════════════════════════════════
// Edge session marker + PKCE handoff (Shepherd remediation)
// ════════════════════════════════════════════
// pam_authed: a non-sensitive presence marker so the CloudFront Lambda@Edge gate can tell
// an authenticated browser from an unauthenticated one WITHOUT reading sessionStorage
// (which the edge cannot see). Holds no token/secret. The edge redirects to Federate only
// when this cookie is absent; the Bearer token the API authorizer checks remains the real
// security boundary. Cleared on logout/expiry so the edge re-challenges.
// pam_pkce: the edge OWNS PKCE — it sets the code_verifier here so this app's exchange can
// send the MATCHING verifier for the challenge the edge sent.
const EDGE_MARKER_COOKIE = 'pam_authed';
const EDGE_PKCE_COOKIE = 'pam_pkce';

const setEdgeMarker = () => {
    try { document.cookie = `${EDGE_MARKER_COOKIE}=1; Path=/; Secure; SameSite=Lax`; } catch { /* ignore */ }
};
const clearEdgeMarker = () => {
    try { document.cookie = `${EDGE_MARKER_COOKIE}=; Path=/; Secure; SameSite=Lax; Max-Age=0`; } catch { /* ignore */ }
};
const readCookie = (name) => {
    try {
        const parts = (document.cookie || '').split(';');
        for (let i = 0; i < parts.length; i++) {
            const c = parts[i].trim();
            const eq = c.indexOf('=');
            if (eq > -1 && c.slice(0, eq) === name) return c.slice(eq + 1);
        }
    } catch { /* ignore */ }
    return '';
};
const clearEdgePkce = () => {
    try { document.cookie = `${EDGE_PKCE_COOKIE}=; Path=/; Secure; SameSite=Lax; Max-Age=0`; } catch { /* ignore */ }
};

// ════════════════════════════════════════════
// Session management
// ════════════════════════════════════════════
// Decode a JWT payload without verifying (we only need the exp claim; the API Gateway
// authorizer does the real signature check).
const decodeJwtPayload = (token) => {
    try {
        const parts = (token || '').split('.');
        if (parts.length !== 3) return null;
        let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        b64 = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        return JSON.parse(atob(b64));
    } catch {
        return null;
    }
};

const getSession = () => {
    try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (!saved) return null;
        const session = JSON.parse(saved);
        if (session.expiresAt && Date.now() > session.expiresAt) {
            // Access token expired — DON'T wipe storage; the refresh token outlives it and
            // is needed for silent renewal. Just report "no valid session".
            return null;
        }
        return session;
    } catch {
        return null;
    }
};

const setSession = (alias, accessToken, refreshToken) => {
    // Use the token's REAL exp (Federate tokens here are ~15 min), not a fixed 60-min guess.
    const claims = decodeJwtPayload(accessToken);
    const tokenExpMs = (claims && typeof claims.exp === 'number') ? claims.exp * 1000 : null;
    const expiresAt = tokenExpMs || (Date.now() + 15 * 60 * 1000);
    const prev = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; } })();
    const rt = refreshToken || (prev && prev.refreshToken) || '';
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        alias,
        accessToken: accessToken || '',
        refreshToken: rt,
        expiresAt,
        authenticatedAt: new Date().toISOString()
    }));
    // Drop the edge session marker so the CloudFront gate passes this browser through.
    setEdgeMarker();
};

export const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEY);
    clearEdgeMarker();
};

export const getAccessToken = () => {
    const session = getSession();
    return session?.accessToken || '';
};

// Read the refresh token from raw storage (must survive access-token expiry).
const getRefreshToken = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return '';
        return JSON.parse(raw).refreshToken || '';
    } catch {
        return '';
    }
};

const getStoredAlias = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (raw) { const a = JSON.parse(raw).alias; if (a) return a; }
    } catch { /* ignore */ }
    return localStorage.getItem('approver_alias') || '';
};

// ════════════════════════════════════════════
// Silent refresh — renew the short-lived access token via the backend refreshToken op
// using the stored refresh_token. No redirect. Single-flight.
// ════════════════════════════════════════════
let _refreshInFlight = null;

export const silentRefresh = async () => {
    const rt = getRefreshToken();
    if (!rt) return null;
    if (_refreshInFlight) return _refreshInFlight;

    _refreshInFlight = (async () => {
        try {
            const resp = await fetch(`${API_BASE_URL}/auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operation: 'refreshToken', refreshToken: rt, clientId: CLIENT_ID })
            });
            const data = await resp.json();
            const result = typeof data.body === 'string' ? JSON.parse(data.body) : data;
            if (result && result.success && result.accessToken) {
                const alias = result.alias || getStoredAlias();
                setSession(alias, result.accessToken, result.refreshToken || rt);
                if (alias) localStorage.setItem('approver_alias', String(alias).toLowerCase());
                console.log('[Auth] Silent refresh OK');
                return result.accessToken;
            }
            console.warn('[Auth] Silent refresh returned no token (reauth needed)');
            return null;
        } catch (e) {
            console.warn('[Auth] Silent refresh error:', e.message);
            return null;
        } finally {
            _refreshInFlight = null;
        }
    })();

    return _refreshInFlight;
};

// Ensure a usable (non-expired) access token before an API call; silently refresh if needed.
export const ensureFreshToken = async () => {
    const session = getSession();
    if (session && session.accessToken && session.expiresAt && (session.expiresAt - Date.now()) > 60 * 1000) {
        return session.accessToken;
    }
    const refreshed = await silentRefresh();
    return refreshed || '';
};

// ════════════════════════════════════════════
// Proactive auto-refresh — the fix for the "idle tab / came back later, it failed" bug.
//
// A setTimeout-only strategy is unreliable because browsers THROTTLE/SUSPEND timers in
// backgrounded or idle tabs, so the token silently expires and the next action 401s.
// We defend on three fronts:
//   1) A recurring interval that refreshes when the token is near expiry (works while the
//      tab is foreground/active).
//   2) A `visibilitychange`/`focus` handler that refreshes the moment the user returns to
//      the tab (covers the exact "left it idle, came back" case — timers were suspended,
//      so we refresh on wake instead of trusting the timer).
//   3) The pre-flight ensureFreshToken() on every API call (defensive last line).
// ════════════════════════════════════════════
let _autoRefreshWired = false;

// Refresh if the current token is expired or within this window of expiry.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;   // refresh when < 5 min remain
const REFRESH_POLL_MS = 60 * 1000;         // check every minute while active

const _tokenNeedsRefresh = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return false;                 // no session → nothing to refresh (login flow handles it)
        const s = JSON.parse(raw);
        if (!s.refreshToken) return false;      // can't silently refresh without a refresh token
        if (!s.expiresAt) return true;
        return (s.expiresAt - Date.now()) < REFRESH_MARGIN_MS;
    } catch { return false; }
};

const _maybeRefresh = async (reason) => {
    if (_tokenNeedsRefresh()) {
        console.log(`[Auth] Proactive refresh (${reason})`);
        await silentRefresh();
    }
};

// Wire up visibility/focus/interval-based auto-refresh. Call once at app startup.
export const initAuthAutoRefresh = () => {
    if (_autoRefreshWired) return;
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;
    _autoRefreshWired = true;

    // (2) Refresh when the user returns to the tab — the key fix for idle-tab expiry.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') _maybeRefresh('visibilitychange');
    });
    window.addEventListener('focus', () => _maybeRefresh('focus'));

    // (1) Also poll while active. Note: throttled/suspended in background — that's fine,
    // the visibility handler covers the return-to-tab case.
    setInterval(() => _maybeRefresh('interval'), REFRESH_POLL_MS);

    // Kick one check now (e.g., page was restored from bfcache with a stale token).
    _maybeRefresh('init');
};

// ════════════════════════════════════════════
// Get current user alias
// ════════════════════════════════════════════
export const getCurrentUser = async () => {
    // Skip auth on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return localStorage.getItem('approver_alias') || null;
    }

    // 1. Check existing session
    const session = getSession();
    if (session && session.alias) return session.alias;

    // 2. Check if returning from Federate with auth code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
        const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || window.location.pathname;
        sessionStorage.removeItem(RETURN_URL_KEY);
        window.history.replaceState({}, '', returnUrl);

        try {
            console.log('[Auth] Exchanging code...');
            const authResult = await exchangeCodeForAlias(code);
            console.log('[Auth] Got alias:', authResult?.alias);
            if (authResult && authResult.alias) {
                setSession(authResult.alias, authResult.accessToken, authResult.refreshToken);
                localStorage.setItem('approver_alias', authResult.alias.toLowerCase());
                sessionStorage.removeItem(ATTEMPTED_KEY);
                return authResult.alias;
            }
        } catch (err) {
            console.error('[Auth] Exchange failed:', err.message);
        }

        return localStorage.getItem('approver_alias') || null;
    }

    // 3. Redirect to Federate (only once)
    if (sessionStorage.getItem(ATTEMPTED_KEY)) {
        sessionStorage.removeItem(ATTEMPTED_KEY);
        return localStorage.getItem('approver_alias') || null;
    }

    sessionStorage.setItem(ATTEMPTED_KEY, 'true');
    sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
    redirectToFederate();
    return null;
};

// ════════════════════════════════════════════
// Redirect to Federate
// ════════════════════════════════════════════
const redirectToFederate = () => {
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    generateCodeChallenge(codeVerifier).then(codeChallenge => {
        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            response_type: 'code',
            redirect_uri: REDIRECT_URI,
            // Request offline_access so Federate returns a refresh_token for silent renewal
            // of the short-lived (~15 min) access token.
            scope: 'openid offline_access',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256'
        });
        window.location.replace(`${FEDERATE_AUTHORIZE}?${params.toString()}`);
    });
};

// ════════════════════════════════════════════
// Exchange code for alias via Lambda
// ════════════════════════════════════════════
const exchangeCodeForAlias = async (code) => {
    // Prefer the edge-provided PKCE verifier (pam_pkce cookie) set by the Lambda@Edge gate
    // that owned this redirect; fall back to the app's own sessionStorage verifier for any
    // app-initiated (non-edge) login path.
    const edgeVerifier = readCookie(EDGE_PKCE_COOKIE);
    const codeVerifier = edgeVerifier || sessionStorage.getItem('pkce_code_verifier') || '';
    sessionStorage.removeItem('pkce_code_verifier');
    clearEdgePkce();

    const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            operation: 'exchangeAuthCode',
            code,
            redirectUri: REDIRECT_URI,
            codeVerifier,
            clientId: CLIENT_ID
        })
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Auth exchange failed: ${response.status} ${text}`);
    }

    const data = await response.json();
    const result = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return {
        alias: result.alias || null,
        accessToken: result.accessToken || '',
        refreshToken: result.refreshToken || '',
    };
};

// ════════════════════════════════════════════
// PKCE helpers
// ════════════════════════════════════════════
const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
};

const base64UrlEncode = (buffer) => {
    return btoa(String.fromCharCode(...buffer))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};
